/**
 * usePermissions - Hook for permission-based access control
 *
 * Provides permission checking based on user role and explicit overrides.
 * Uses the permissions object returned by /api/auth/me/.
 */

import { useMemo } from "react";
import { useAuth } from "../auth";

/**
 * Hook for checking user permissions.
 *
 * @returns {Object} Permission helpers and checks
 * @example
 * const { canEditPhotos, canDeletePhotos, getClickTarget } = usePermissions();
 * if (canEditPhotos) { ... }
 */
export function usePermissions() {
  const { user } = useAuth();

  return useMemo(() => {
    const permissions = user?.permissions || {};
    const role = user?.role || null;

    // Permission checks (with fallback to false)
    const canViewPhotos = permissions.can_view_photos ?? false;
    const canEditPhotos = permissions.can_edit_photos ?? false;
    const canDeletePhotos = permissions.can_delete_photos ?? false;
    const canArchivePhotos = permissions.can_archive_photos ?? false;
    const canApprovePhotos = permissions.can_approve_photos ?? false;
    const canLabelFaces = permissions.can_label_faces ?? false;
    const canManagePersons = permissions.can_manage_persons ?? false;
    const canDeletePersons = permissions.can_delete_persons ?? false;
    const canMergePersons = permissions.can_merge_persons ?? false;
    const canManageTeams = permissions.can_manage_teams ?? false;
    const canManageMatches = permissions.can_manage_matches ?? false;
    const canManageCompany = permissions.can_manage_company ?? false;
    const canManageProfiles = permissions.can_manage_profiles ?? false;
    const canSetDefaultProfile = permissions.can_set_default_profile ?? false;
    const canEmptyEnvironment = permissions.can_empty_environment ?? false;

    // Page access helpers
    const canAccessEdit = canEditPhotos;
    const canAccessView = canViewPhotos;
    const canAccessMatches = canManageMatches || role === "editor" || role === "editor_photographer" || role === "photographer";
    const canAccessManage = true; // All roles can access /manage (but different tabs)
    const canAccessTraining = true; // All roles can access training
    const canAccessCompanySettings = canManageCompany;
    const canAccessTeams = canManageTeams;
    const canAccessPersons = canManagePersons;

    // Default view preference based on role
    const getDefaultView = () => {
      if (role === "company_manager" || role === "planner") {
        return "edit";
      }
      return "viz";
    };

    // Determine click target for photo cards based on role and photo status
    const getClickTarget = (photo) => {
      // Managers and planners always go to /view
      if (role === "company_manager" || role === "planner") {
        return "/view";
      }
      // Photographers go to /view if not done, /edit if done
      if (role === "photographer") {
        return photo?.status === "done" ? "/edit" : "/view";
      }
      // Editors and editor_photographers go to /edit
      return "/edit";
    };

    // Check if user can edit a specific photo
    const canEditPhoto = (photo) => {
      if (!canEditPhotos) return false;

      // Photographers can only edit their own done photos
      if (role === "photographer") {
        return photo?.status === "done";
      }

      return true;
    };

    // Check if photo should be read-only for this user
    const isPhotoReadOnly = (photo) => {
      if (!photo) return true;

      // Photographer: read-only for non-done photos
      if (role === "photographer" && photo.status !== "done") {
        return true;
      }

      return !canEditPhotos;
    };

    return {
      // Raw data
      user,
      role,
      permissions,

      // Photo permissions
      canViewPhotos,
      canEditPhotos,
      canDeletePhotos,
      canArchivePhotos,
      canApprovePhotos,

      // Face/Person permissions
      canLabelFaces,
      canManagePersons,
      canDeletePersons,
      canMergePersons,

      // Admin permissions
      canManageTeams,
      canManageMatches,
      canManageCompany,
      canManageProfiles,
      canSetDefaultProfile,
      canEmptyEnvironment,

      // Page access
      canAccessEdit,
      canAccessView,
      canAccessMatches,
      canAccessManage,
      canAccessTraining,
      canAccessCompanySettings,
      canAccessTeams,
      canAccessPersons,

      // Helpers
      getDefaultView,
      getClickTarget,
      canEditPhoto,
      isPhotoReadOnly,
    };
  }, [user]);
}

export default usePermissions;
